import axios from 'axios'
import moment from 'moment'
import { get } from 'lodash'
import endpoints from '../configuration/endpoints'
import triggerCustomGaEvent from '../utils/triggerCustomGaEvent'
import formatPackage from '../utils/formatPackage'
import { getInitialPackageData } from '../reducers/initialPackage'
import { addPackage } from './addPackage'
import { currencySymbol } from '../configuration/currencies'
export const RELATED_SAILINGS_ERROR = 'RELATED_SAILINGS_ERROR'
export const REQUEST_RELATED_SAILINGS = 'REQUEST_RELATED_SAILINGS'
export const FINISHED_RELATED_SAILINGS_REQUESTS =
  'FINISHED_RELATED_SAILINGS_REQUESTS'

const sailDateFormat = 'YYYY-MM-DD'

// We have a separate loading and error state for related packages found in the
// relatedSailings reducer. The sailings themselves are added to availablePackages
export const requestRelatedSailings = reqBody => async (dispatch, getState) => {
  const state = getState()
  const { occupancy } = state.activeSearchParameters

  dispatch({
    type: REQUEST_RELATED_SAILINGS
  })
  triggerCustomGaEvent('relatedSailingsRequested')

  let priorRelateSailing
  const nextSailings = []
  // Filter our related sailings
  for (const sailing of reqBody.sailDates) {
    // we need to filter out duplicate days in our related sailing as they don't return any data at the
    // time of this comment
    if (
      reqBody.sailDates.some(
        otherSailing =>
          otherSailing.sailDate === sailing.sailDate &&
          otherSailing.id !== sailing.id
      )
    )
      continue

    // priorRelateSailing => related sailing is before our main sailing but after our nextSailings if we have one already
    const relatedSailingDate = moment(sailing.sailDate, sailDateFormat)
    const mainSailDate = moment(reqBody.sailDate, sailDateFormat)
    if (
      relatedSailingDate.isBefore(mainSailDate) &&
      (!priorRelateSailing ||
        relatedSailingDate.isAfter(priorRelateSailing.sailDate))
    ) {
      priorRelateSailing = sailing
    }

    // nextSailings => related sailing is after our main sailing, we'll add them all here then filter and grab the first four
    if (relatedSailingDate.isAfter(mainSailDate)) {
      nextSailings.push(sailing)
    }
  }

  // sort and grab 4
  const sortedNextSailings = nextSailings
    .sort((a, b) => {
      if (
        moment(a.sailDate, sailDateFormat).isBefore(
          moment(b.sailDate, sailDateFormat),
          'day'
        )
      ) {
        return -1
      }
      return 1
    })
    .slice(0, 4)

  const filteredRelatedSailing = priorRelateSailing
    ? [priorRelateSailing, ...sortedNextSailings]
    : sortedNextSailings

  // set up our request parameters
  const fetchQuery = new URLSearchParams()

  for (const parameter of Object.entries(reqBody)) {
    const key = parameter[0]
    const param = parameter[1]
    if (
      typeof key === 'undefined' ||
      key === 'sailDates' ||
      key === 'sailDate' ||
      key === 'id'
    )
      continue
    fetchQuery.append(key, param)
  }
  fetchQuery.append(
    'countryCode',
    get(state, 'agencyDetails.agent.country', '')
  )

  /*
   * for each filteredRelatedSailing we need to make an api call to two endpoints
   * /api/cruise/FL07G016/pricing?brand=C&countryCode=USA&shipCode=FL&sailDate=2019-12-08
   * and
   * /api/cruise/FL15G016?brand=C
   *
   * Because we will likely have duplicate id's and to avoid making duplicate calls to /api/cruise/FL15G016?brand=C
   * we'll first sort our request into an array with only unique requests.
   * */

  // make an array of cruise endpoint requests and pricing endpoint request
  // axios all and sort by ID.
  const agencyId = reqBody.agencyId || ''
  const officeCode = reqBody.officeCode || ''

  const relatedSailingsRequestArray = filteredRelatedSailing.reduce(
    (arr, relatedSailing) => {
      //  make cruise url
      const cruiseEndpoint = `${endpoints.cruise}/${relatedSailing.id}?brand=${
        reqBody.brand
      }&agencyId=${agencyId}&officeCode=${officeCode}`
      //  make pricing url
      const pricingEndpoint = `${endpoints.cruise}/${
        relatedSailing.id
      }/vps-pricing?${fetchQuery.toString()}&sailDate=${
        relatedSailing.sailDate
      }&occupancy=${occupancy}&agencyId=${agencyId}&officeCode=${officeCode}`

      if (!arr.some(request => request === cruiseEndpoint)) {
        arr.push(cruiseEndpoint)
      }
      if (!arr.some(request => request === pricingEndpoint)) {
        arr.push(pricingEndpoint)
      }
      return arr
    },
    []
  )

  const promiseList = relatedSailingsRequestArray.map(sailingRequest =>
    axios.get(sailingRequest)
  )

  const initialCruise = getInitialPackageData(state)

  await axios
    .all(promiseList)
    .then(responses => {
      for (const response of responses) {
        // get the pricing responses and find it's matching cruise data
        if (
          response.data &&
          response.data.package &&
          !response.data.package.id // only CruiseDetails have an id on the package so we'll skip those at first
        ) {
          const cruiseData = responses.find(
            res =>
              res.data &&
              res.data.package &&
              res.data.package.id &&
              res.data.package.id === response.data.id
          )

          if (cruiseData) {
            const { brand, sailDate } = response.data
            const {
              currency,
              selections,
              postTourName,
              preTourName,
              destinationName,
              shipName
            } = initialCruise
            const formattedPackage = formatPackage({
              brand,
              sailDate,
              currency,
              currencySymbol: currencySymbol[currency].symbol || '$',
              currencySymbolPlacement:
                currencySymbol[currency].placement || 'start',
              selections,
              postTourName,
              preTourName,
              destinationName,
              shipName,
              ...cruiseData.data.package,
              pricing: response.data.package,
              occupancy,
              itinerary:
                cruiseData.data.package.itinerary &&
                cruiseData.data.package.itinerary.events
                  ? cruiseData.data.package.itinerary.events
                  : []
            })

            // for some reason the related sailing itinerary comes back in a different shape
            // if the .events array exists we'll move it up one level
            if (
              formattedPackage &&
              formattedPackage.itinerary &&
              formattedPackage.itinerary.events
            ) {
              formattedPackage.itinerary = formattedPackage.itinerary.events
            }

            dispatch(addPackage(formattedPackage))
          }
        }
      }
    })
    .catch(() => {
      dispatch({
        type: RELATED_SAILINGS_ERROR
      })
    })
  dispatch({
    type: FINISHED_RELATED_SAILINGS_REQUESTS
  })
}

export const formatStateRoomsForRelatedSailings = stateRooms => {
  return stateRooms.reduce((obj, room) => {
    if (
      !obj[room.stateroomType] ||
      obj[room.stateroomType].netPrice > room.netPrice
    ) {
      obj[room.stateroomType] = {
        ...room
      }
    }
    return obj
  }, {})
}
